import { z } from 'zod';

import { DateLikeSchema, ValidUuidOrNotSetSchema } from '@vestigas/utils';
import { i18n } from '~/utils';

import { AddressModel } from './zodAddressModel';
import { LineItemModel } from './zodLineItemModel';
import { TradeAddressModel } from './zodTradeAddressModel';

export const LicensePlateSchemaGermany = z
  .string()
  .regex(/^[A-ZÄÖÜ]{1,3}-[A-ZÄÖÜ]{1,3} \d{1,5}( (E|H))?$/, {
    message: i18n.t(
      'aiParserReview.form.carrierLicensePlate.validationMessage',
    ),
  });

/**
 * Zod validation schema for parties.
 */
const PartyModel = z.object({
  id: z.uuid(),
  legalOrganization: z.object({
    address: AddressModel.optional(),
    companyAccountId: z.uuid(),
    id: z.uuid(),
    name: z.string().trim().min(1),
  }),
});

/**
 * Zod validation schema for optional parties.
 */
const PartyModelOptional = z.object({
  id: ValidUuidOrNotSetSchema,
  legalOrganization: z.object({
    address: AddressModel.optional(),
    companyAccountId: ValidUuidOrNotSetSchema,
    id: ValidUuidOrNotSetSchema,
    name: z.string().trim().optional(), // legalOrganization.name key must always be present,
  }),
});

/**
 * Zod validation schema for parties that do not have to be in our database.
 * IDs are not required, only a legalOrganization must be given.
 */
const PartyModelFreeform = z.object({
  id: ValidUuidOrNotSetSchema,
  legalOrganization: z.object({
    address: AddressModel.optional(),
    companyAccountId: ValidUuidOrNotSetSchema,
    id: ValidUuidOrNotSetSchema,
    name: z.string().trim().min(1),
  }),
});

/**
 * Zod validation schema for delivery note.
 */
export const DeliveryNoteModel = (parserFormat: 'ai' | 'ocr' = 'ocr') =>
  z.object({
    dlnDate: DateLikeSchema,
    header: z.object({
      id: z.string().trim().min(1),
      issuer: PartyModelOptional,
      technicalIssuer: PartyModel.optional(),
    }),
    lineItems: z.array(LineItemModel),
    transaction: z.object({
      agreement: z.object({
        buyer: PartyModel,
        deliveryTerms: z.object({
          code: z.string().trim().optional(), // SHOULD be DeliveryTypeType, but we are more lenient here
        }),
        seller: parserFormat === 'ai' ? PartyModel : PartyModelFreeform,
        trader: PartyModelOptional,
      }),
      delivery: z.object({
        execution: z.object({
          arrived: DateLikeSchema.optional(),
          arrivedPlanned: DateLikeSchema.optional(),
          beginExecution: DateLikeSchema.optional(),
          beginExecutionPlanned: DateLikeSchema.optional(),
          beginLoading: DateLikeSchema.optional(),
          beginLoadingPlanned: DateLikeSchema.optional(),
          beginUnloading: DateLikeSchema.optional(),
          beginUnloadingPlanned: DateLikeSchema.optional(),
          co2Emission: z.object({
            value: z.number().or(z.string()).optional(),
            measure: z.string().optional(),
          }),
          co2EmissionPlanned: z.object({
            value: z.number().or(z.string()).optional(),
            measure: z.string().optional(),
          }),
          departure: DateLikeSchema.optional(),
          departurePlanned: DateLikeSchema.optional(),
          distance: z.object({
            value: z.number().or(z.string()).optional(),
            measure: z.string().optional(),
          }),
          distancePlanned: z.object({
            value: z.number().or(z.string()).optional(),
            measure: z.string().optional(),
          }),
          endExecution: DateLikeSchema.optional(),
          endExecutionPlanned: DateLikeSchema.optional(),
          endLoading: DateLikeSchema.optional(),
          endLoadingPlanned: DateLikeSchema.optional(),
          endUnloading: DateLikeSchema.optional(),
          endUnloadingPlanned: DateLikeSchema.optional(),
          waitingTime: z.number().or(z.string()).optional(),
        }),
        shipFrom: (parserFormat === 'ai'
          ? PartyModelOptional
          : PartyModelFreeform
        ).extend({
          consignment: z.object({
            carrier: PartyModelOptional,
            movement: z.object({ means: z.string().trim().optional() }),
          }),
          tradeAddress: TradeAddressModel.nullish(),
        }),
        shipTo: PartyModel.extend({
          tradeAddress: TradeAddressModel.nullish(),
        }),
      }),
      settlement: z.object({
        salesAccount: z.object({ id: z.string().nullish() }),
      }),
    }),
  });
